// Сторож экрана продукта: ловит асинхронные падения (error / unhandledrejection), которые
// ErrorBoundary не видит, и отправляет обезличенный отчёт на сервер Оболочки.
// Ошибка в первые SCREEN_ATTRIBUTION_MS после mount считается ошибкой экрана, даже если в стеке нет /m/<slug>/.
export const SCREEN_ATTRIBUTION_MS = 15000;
export const SCREEN_READY_TIMEOUT_MS = 15000;
// Один отчёт на (product, message_hash) за это окно, на вкладку.
export const REPORT_DEDUP_MS = 5 * 60 * 1000;
const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
// Синхронный SHA-256: crypto.subtle недоступен на http-стендах, а отчёт не должен зависеть от контекста.
export function sha256Hex(input) {
    const data = new TextEncoder().encode(input);
    const bitLen = data.length * 8;
    const padded = new Uint8Array(((data.length + 9 + 63) >> 6) << 6);
    padded.set(data);
    padded[data.length] = 0x80;
    const view = new DataView(padded.buffer);
    view.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000));
    view.setUint32(padded.length - 4, bitLen >>> 0);
    const h = new Uint32Array([0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19]);
    const w = new Uint32Array(64);
    const rotr = (x, n) => (x >>> n) | (x << (32 - n));
    for (let off = 0; off < padded.length; off += 64) {
        for (let i = 0; i < 16; i++)
            w[i] = view.getUint32(off + i * 4);
        for (let i = 16; i < 64; i++) {
            const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
            const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
        }
        let [a, b, c, d, e, f, g, hh] = h;
        for (let i = 0; i < 64; i++) {
            const t1 = (hh + (rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25)) + ((e & f) ^ (~e & g)) + K[i] + w[i]) >>> 0;
            const t2 = ((rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22)) + ((a & b) ^ (a & c) ^ (b & c))) >>> 0;
            hh = g;
            g = f;
            f = e;
            e = (d + t1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (t1 + t2) >>> 0;
        }
        h[0] += a;
        h[1] += b;
        h[2] += c;
        h[3] += d;
        h[4] += e;
        h[5] += f;
        h[6] += g;
        h[7] += hh;
    }
    return Array.from(h, (x) => x.toString(16).padStart(8, "0")).join("");
}
export function messageHash(message) {
    return sha256Hex(message.slice(0, 200)).slice(0, 16);
}
export function uaFamily(ua) {
    if (/Firefox|FxiOS/.test(ua))
        return "firefox";
    if (/Chrome|Chromium|CriOS|Edg\//.test(ua))
        return "chrome";
    if (/Safari/.test(ua))
        return "safari";
    return "other";
}
function errorClass(err) {
    const name = err instanceof Error ? err.name : "";
    return /^[A-Za-z0-9_]{1,64}$/.test(name) ? name : "Error";
}
function messageOf(err, fallback = "") {
    if (err instanceof Error)
        return err.message;
    if (typeof err === "string")
        return err;
    return fallback;
}
export function createReporter(env) {
    const sent = new Map();
    return function report(input) {
        const hash = messageHash(messageOf(input.error, input.fallbackMessage));
        const key = `${input.product}|${hash}`;
        const now = env.now();
        const prev = sent.get(key);
        if (prev !== undefined && now - prev < REPORT_DEDUP_MS)
            return false;
        sent.set(key, now);
        const shellVersion = env.shellVersion?.();
        const body = {
            product: input.product,
            ...(input.productVersion ? { product_version: input.productVersion } : {}),
            ...(shellVersion ? { shell_version: shellVersion } : {}),
            kind: input.kind,
            error_class: errorClass(input.error),
            message_hash: hash,
            route: env.route(),
            ua_family: uaFamily(env.ua()),
        };
        try {
            env.send(body);
        }
        catch { /* отчёт не должен ронять экран */ }
        return true;
    };
}
const browserEnv = {
    now: () => Date.now(),
    send: (body) => {
        // Отказ молча: канал отчёта не должен порождать новых ошибок.
        fetch("/api/errors/front", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
            credentials: "same-origin",
            keepalive: true,
        }).catch(() => { });
    },
    route: () => window.location.pathname,
    ua: () => navigator.userAgent,
    shellVersion: () => window.__OSNOVA_SHELL_VERSION__,
};
export const reportFrontError = createReporter(browserEnv);
export function startScreenWatch(opts) {
    const target = opts.target ?? window;
    const now = opts.now ?? (() => Date.now());
    const setTimer = opts.setTimer ?? ((cb, ms) => setTimeout(cb, ms));
    const clearTimer = opts.clearTimer ?? ((id) => clearTimeout(id));
    const report = opts.report ?? reportFrontError;
    const startedAt = now();
    const marker = `/m/${opts.slug}/`;
    let disposed = false;
    const fail = (kind, error, fallbackMessage) => {
        if (disposed)
            return;
        report({ product: opts.slug, productVersion: opts.productVersion, kind, error, fallbackMessage });
        opts.onFail();
    };
    const belongs = (error, extra) => {
        const stack = error instanceof Error ? error.stack ?? "" : "";
        if (stack.includes(marker) || (extra ?? "").includes(marker))
            return true;
        return now() - startedAt <= SCREEN_ATTRIBUTION_MS;
    };
    const onError = (e) => {
        if (belongs(e.error, e.filename))
            fail("error", e.error, e.message);
    };
    const onRejection = (e) => {
        if (belongs(e.reason))
            fail("unhandled_rejection", e.reason);
    };
    target.addEventListener("error", onError);
    target.addEventListener("unhandledrejection", onRejection);
    let timer = setTimer(() => {
        timer = undefined;
        if (opts.enforceReadyTimeout)
            fail("error", new Error("screen ready timeout"));
    }, SCREEN_READY_TIMEOUT_MS);
    return {
        ready() {
            if (timer !== undefined) {
                clearTimer(timer);
                timer = undefined;
            }
        },
        dispose() {
            if (disposed)
                return;
            disposed = true;
            target.removeEventListener("error", onError);
            target.removeEventListener("unhandledrejection", onRejection);
            if (timer !== undefined) {
                clearTimer(timer);
                timer = undefined;
            }
        },
    };
}
